import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { Badge } from '@plato/ui-legacy/components/list-views/badge';
import { CollapsibleText } from '@plato/ui-legacy/components/ui/collapsible-text';
import Text from '@plato/ui-legacy/components/ui/text';
import { roundAbsToHundred } from '@plato/ui-legacy/lib/round-abs-to-hundred';
import { Sparkles } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { InsInsightOutcomeDTO } from '../../../server/dto/ins-insight-outcome-dto.ts';
import type { ActivityFeedRecord } from '../../../server/types/customer-activity-feed-types.ts';
import { useDialogs } from '../../dialogs/all-dialogs.tsx';
import { getInsightOutcomeEntityName } from '../../insights-common/lib/insight-entity-name.ts';
import { getInsightName } from '../../insights-common/lib/insight-name.ts';
import {
    getInsightOutcomeStatusColor,
    useInsightOutcomeResponseLabel,
} from '../../insights-common/lib/insight-outcome.ts';
import { RichTextViewer } from '../../rich-text/components/rich-text-viewer.tsx';
import { TimelineList } from '../../shared/components/timeline-list/timeline-list.tsx';

export const ActivityFeedInsightOutcomeItem = React.forwardRef<
    HTMLDivElement,
    { activity: ActivityFeedRecord }
>(({ activity }, ref) => {
    const { t } = useTranslation('insights');
    const { enableNeverBoughtCrossSellCopy } = useAppConfig();
    const { openInsightOutcomeDetails } = useDialogs();
    const outcome = activity.payload as InsInsightOutcomeDTO;

    const responseLabel = useInsightOutcomeResponseLabel(outcome.response);

    return (
        <TimelineList.Item
            icon={Sparkles}
            ref={ref}
            role="article"
            onClick={() => {
                openInsightOutcomeDetails(outcome.id);
            }}
        >
            <TimelineList.ItemHeader>
                <div className="flex w-full items-center justify-between gap-2">
                    <Text weight="semibold">{getInsightOutcomeEntityName(t, outcome)}</Text>
                    {responseLabel && (
                        <Badge color={getInsightOutcomeStatusColor(outcome.response)}>{responseLabel}</Badge>
                    )}
                </div>
            </TimelineList.ItemHeader>
            <TimelineList.ItemBody>
                <div className="flex flex-col gap-2">
                    {outcome.textRich && (
                        <CollapsibleText>
                            <RichTextViewer content={outcome.textRich} />
                        </CollapsibleText>
                    )}
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-neutral-600">
                        <Text color="inherit">
                            <CurrencyOrEmpty>
                                {outcome.insight.absolute_number
                                    ? Math.abs(roundAbsToHundred(outcome.insight.absolute_number))
                                    : null}
                            </CurrencyOrEmpty>{' '}
                            {t('insight_outcome__est_value')}
                        </Text>
                        <span aria-hidden>•</span>
                        <Text color="inherit">
                            {getInsightName(t, outcome.insight, {
                                neverBoughtCrossSell: enableNeverBoughtCrossSellCopy,
                            })}
                        </Text>
                        <span aria-hidden>•</span>
                        <Text color="inherit">
                            {t('insight_outcome__resolved_by', {
                                user: outcome.createdBy.fullName,
                            })}
                        </Text>
                    </div>
                </div>
            </TimelineList.ItemBody>
        </TimelineList.Item>
    );
});

ActivityFeedInsightOutcomeItem.displayName = 'ActivityFeedInsightOutcomeItem';
